import OtpInput from './OtpInput';
import React, { useState, useEffect } from 'react';
import OTPHeader from './OTPHeader';
import { Form, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useOTPContext } from '../../contexts/OtpContext';
import {
  useSendLoginOTP,
  useResendOTP,
  useSendForgetPasswordOTP,
  useResendForgetPasswordOTP,
} from '../../api/hooks/useUserQuery';
import { useSetUserAndToken } from '../../pages/signIn/helpers/signinHelper';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { SignInStageEnum } from '../../enums/SignInStage';
import { getServerErrorMsg } from '../../helpers/errorHandler';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { UsersStatusEnum } from '../../enums/UserStatusEnum';

interface OTPCardProps {
  logo: string;
  title: string;
  description: string | JSX.Element;
  length: number;
  buttonText?: string;
  isForgetPassword: boolean;
  onChangeStage: Function;
}

const OTPCard: React.FC<OTPCardProps> = ({
  logo,
  title,
  description,
  length,
  buttonText = 'Submit',
  isForgetPassword,
  onChangeStage,
}) => {
  const [otp, setOtp] = useState<string>('');
  const { t } = useTranslation(['signIn', 'common', 'form']);
  const [timer, setTimer] = useState(ENVIRONMENT_CONFIG.RESEND_TIMER);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [resetOTP, setResetOTP] = useState(true);
  const { successNotification, errorNotification } = useNotificationContext();
  const useSendOTP = useSendLoginOTP();
  const useResend = useResendOTP();
  const useResendForgetPassword = useResendForgetPasswordOTP();
  const useSendForgetOTP = useSendForgetPasswordOTP();
  const { setUserAndToken } = useSetUserAndToken();
  const {
    getSigninOtpId,
    getForgetPasswordOtpId,
    getOTPCreateTime,
    handleSetSigninOtpId,
    handleSetForgetPasswordOtpId,
    handleSetOtp,
    getEmail,
    handleSetEmail,
    handleSetOtpCreateTime,
  } = useOTPContext();

  const handleOtpChange = (otpValue: string) => {
    setOtp(otpValue);
  };

  const handleSubmit = () => {
    if (otp.length === length) {
      if (isForgetPassword) {
        handleForgetOtpComplete(otp, getForgetPasswordOtpId());
      } else {
        handleOtpComplete(otp, getSigninOtpId());
      }
    } else {
      console.error('OTP is incomplete');
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  useEffect(() => {
    let interval: number | undefined;
    if (isTimerRunning) {
      interval = window.setInterval(() => {
        setTimer((prev: number) => {
          if (prev <= 1) {
            clearInterval(interval);
            setIsTimerRunning(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTimerRunning]);

  useEffect(() => {
    const otpCreateTime = getOTPCreateTime();
    if (!otpCreateTime) return;
    const elapsedTime = Math.floor((Date.now() - otpCreateTime) / 1000);
    const remainingTime = Math.max(ENVIRONMENT_CONFIG.RESEND_TIMER - elapsedTime, 0);
    setTimer(remainingTime);
    setIsTimerRunning(remainingTime > 0);
  }, [getOTPCreateTime]);

  const handleOtpComplete = (otp: string, otpId: number) => {
    const email = getEmail();
    useSendOTP.mutate(
      { email, otp, otpId },
      {
        onSuccess: (data: any) => {
          if (!otpId) {
            console.error(t('otpIdIsUndefined'));
            return;
          }
          if (data.data.message != null) {
            errorNotification(data.data.message);
            return;
          }

          if (data.data.statusCode === 200) {
            successNotification(t('OTPVerified'));
            const dataObject = data.data.data;
            setUserAndToken(
              dataObject.user,
              dataObject.token,
              dataObject.allowPermission,
              dataObject.deviceToken
            );
            return;
          }
        },
        onError: (response) => {
          const errorMsg = getServerErrorMsg(response);
          errorNotification(errorMsg);
        },
      }
    );
  };

  const handleForgetOtpComplete = (otp: string, otpId: number) => {
    const email = getEmail();
    useSendForgetOTP.mutate(
      { email, otp, otpId },
      {
        onSuccess: (data: any) => {
          if (!otpId) {
            console.error(t('otpIdIsUndefined'));
            return;
          }
          if (data.data.message != null) {
            errorNotification(data.data.message);
            return;
          }

          if (data.data.statusCode === 200) {
            successNotification(t('forgetOTPVerified'));
            const dataObject = data.data.data;
            handleSetForgetPasswordOtpId(dataObject.otpId);
            handleSetEmail(email);
            handleSetOtp(dataObject.otp);
            onChangeStage(SignInStageEnum.RESET_PASSWORD);
            return;
          }
        },
        onError: (response) => {
          const errorMsg = getServerErrorMsg(response);
          errorNotification(errorMsg);
        },
      }
    );
  };

  const handleResendOtp = () => {
    const otpId = getSigninOtpId();
    useResend.mutate(
      { otpId },
      {
        onSuccess: (response: any) => {
          if (response.data.data.userStatus === UsersStatusEnum.BLOCKED) {
            errorNotification(t('accountBlocked'));
            onChangeStage(SignInStageEnum.SIGN_IN);
            return;
          }
          successNotification(t('otpResent'));
          handleSetSigninOtpId(response.data.data.otpId);
          handleSetOtpCreateTime(response.data.data.createTime);
          setIsTimerRunning(true);
        },
        onError: (response) => {
          const errorMsg = getServerErrorMsg(response);
          errorNotification(errorMsg);
        },
      }
    );
  };

  const handleResendForgetPasswordOtp = () => {
    const forgetOtpId = getForgetPasswordOtpId();
    useResendForgetPassword.mutate(
      { otpId: forgetOtpId },
      {
        onSuccess: (response: any) => {
          if (response.data.data.userStatus === UsersStatusEnum.BLOCKED) {
            errorNotification(t('accountBlocked'));
            onChangeStage(SignInStageEnum.SIGN_IN);
            return;
          }
          successNotification(t('otpResent'));
          handleSetForgetPasswordOtpId(response.data.data.otpId);
          handleSetOtpCreateTime(response.data.data.createTime);
          setIsTimerRunning(true);
        },
        onError: (response) => {
          const errorMsg = getServerErrorMsg(response);
          errorNotification(errorMsg);
        },
      }
    );
  };

  const handleResend = () => {
    setOtp('');
    setResetOTP(true);
    if (isForgetPassword) {
      handleResendForgetPasswordOtp();
    } else {
      handleResendOtp();
    }
  };

  useEffect(() => {
    if (resetOTP) {
      setTimeout(() => setResetOTP(false), 100);
    }
  }, [resetOTP]);

  return (
    <div className="h-screen-minus-100 from-blue-50 to-gray-100 flex w-full items-center justify-center bg-gradient-to-b">
      <div className="flex h-[600px] w-full flex-col items-center justify-center bg-white p-6">
        <OTPHeader logo={logo} title={title} description={description} />

        <div className="flex w-full flex-col items-center justify-center">
          <OtpInput
            length={length}
            onComplete={handleOtpChange}
            reset={resetOTP}
            className="mt-2 justify-center"
          />
          <Form.Item className="w-[330px]">
            <Button
              className="btn-primary mt-12"
              type="primary"
              htmlType="submit"
              block
              loading={useSendOTP.isPending || useSendForgetOTP.isPending}
              onClick={handleSubmit}
              disabled={otp.length !== length}
            >
              {buttonText}
            </Button>
          </Form.Item>
        </div>
        <div className="mt-4 text-center">
          <p className="text-gray-500 text-base font-medium">
            {t('didntRecieveCode')}{' '}
            {timer < 300 && timer > 0 ? (
              <p className="text-gray-600 mt-2">
                {t('trySendingAfter')} {formatTime(timer)}
              </p>
            ) : (
              <>
                <button
                  className="text-blue-600 font-medium hover:underline"
                  onClick={handleResend}
                >
                  {t('resendOTP')}
                </button>
              </>
            )}
          </p>
        </div>
        <Form.Item className='mt-4'>
          <p className="cursor-pointer text-center text-sm font-medium">
            {t('backTo')}{' '}
            <span
              className="text-sm font-semibold text-primary hover:underline"
              onClick={() => onChangeStage(SignInStageEnum.SIGN_IN, {})}
            >
              {t('signin')}
            </span>
          </p>
        </Form.Item>
      </div>
    </div>
  );
};

export default OTPCard;
